import React, { useEffect, useRef, useState } from "react";
import { Box, CircularProgress } from "@mui/material";
import { toast } from "react-toastify";
import { SpeakerIcon, StopIcon } from "../../../../assets/icon";
import { messageToAudio } from "../../../../axios/chat";
import { Message } from "../../../../types";
import { useAppStore } from "../../../../stores/appStore";
import {
  isBrowserOffline,
  notifyOfflineActionBlocked,
} from "../../../../utils/internetConnection";
import { normalizeText } from "../../../../helpers/messageHelpers";
import { fetchAuthenticatedFileBlob } from "../../../../utils/authenticatedFileUrl";
import {
  claimTtsAudioSession,
  ensureTtsAudioSocketAttached,
  isTtsAudioSessionOwner,
  releaseTtsAudioSession,
  waitForTtsSocketReady,
  type TtsAudioChunkEvent,
  type TtsAudioErrorEvent,
  type TtsSessionOwner,
} from "../../../../utils/ttsAudioChunkBus";
import { toBackendTabId } from "../tabbed-message/normalize";

interface IndependentTtsButtonProps {
  message: Message;
  tabId?: string;
  speakableTextForVersion?: string;
  disabled?: boolean;
}

export type { TtsAudioChunkEvent, TtsAudioErrorEvent };

// How long to wait for the first socket chunk after 202 before giving up.
const FIRST_CHUNK_TIMEOUT_MS = 90_000;

const normalizeTabId = (value?: string | null): string | null => {
  if (value == null || value === "") return null;
  return toBackendTabId(value);
};

const sameTtsSession = (
  payload: { message_id: string; tab_id?: string | null },
  messageId: string,
  tabId?: string,
): boolean => {
  if (payload.message_id !== messageId) return false;
  const payloadTab = normalizeTabId(payload.tab_id);
  const localTab = normalizeTabId(tabId);
  if (payloadTab == null || localTab == null) return true;
  return payloadTab === localTab;
};

export const IndependentTtsButton: React.FC<IndependentTtsButtonProps> = ({
  message,
  tabId,
  speakableTextForVersion,
  disabled = false,
}) => {
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const createdUrlsRef = useRef<string[]>([]);
  const isLoadingRef = useRef(false);
  const isPlayingRef = useRef(false);
  const sessionActiveRef = useRef(false);
  const sessionOwnerRef = useRef<TtsSessionOwner | null>(null);
  const receivedLastRef = useRef(false);
  const nextPlayIndexRef = useRef(0);
  const chunkBlobUrlsRef = useRef<Map<number, string>>(new Map());
  const playChainRef = useRef(Promise.resolve());
  const firstChunkTimerRef = useRef<number | null>(null);
  const gotFirstChunkRef = useRef(false);

  const messageIdRef = useRef(message.message_id);
  const tabIdRef = useRef(tabId);
  messageIdRef.current = message.message_id;
  tabIdRef.current = tabId;

  const [loading, setLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const { setIsAudioLoading, isOnline } = useAppStore();

  const setLoadingState = (val: boolean) => {
    isLoadingRef.current = val;
    setLoading(val);
    setIsAudioLoading(val);
  };

  const setPlayingState = (val: boolean) => {
    isPlayingRef.current = val;
    setIsPlaying(val);
  };

  const clearFirstChunkTimer = () => {
    if (firstChunkTimerRef.current != null) {
      window.clearTimeout(firstChunkTimerRef.current);
      firstChunkTimerRef.current = null;
    }
  };

  const apiRef = useRef<{
    setLoadingState: (val: boolean) => void;
    setPlayingState: (val: boolean) => void;
    playNextChunk: () => Promise<void>;
    enqueueChunk: (chunk: TtsAudioChunkEvent) => Promise<void>;
    resetSession: (emitStop?: boolean) => void;
  }>({
    setLoadingState,
    setPlayingState,
    playNextChunk: async () => {},
    enqueueChunk: async (_chunk: TtsAudioChunkEvent) => {},
    resetSession: (_emitStop?: boolean) => {},
  });

  const revokeCreatedUrls = () => {
    createdUrlsRef.current.forEach((url) => {
      try {
        URL.revokeObjectURL(url);
      } catch {
        // ignore
      }
    });
    createdUrlsRef.current = [];
    chunkBlobUrlsRef.current.clear();
  };

  const stopAudioElement = () => {
    if (!audioRef.current) return;
    audioRef.current.onended = null;
    audioRef.current.onplay = null;
    audioRef.current.onpause = null;
    audioRef.current.onplaying = null;
    audioRef.current.onerror = null;
    audioRef.current.pause();
    audioRef.current.src = "";
    audioRef.current = null;
  };

  const resetSession = (emitStop = true) => {
    const owner = sessionOwnerRef.current;
    sessionActiveRef.current = false;
    receivedLastRef.current = false;
    nextPlayIndexRef.current = 0;
    gotFirstChunkRef.current = false;
    clearFirstChunkTimer();
    if (owner) {
      releaseTtsAudioSession(owner);
      sessionOwnerRef.current = null;
    }
    stopAudioElement();
    revokeCreatedUrls();
    setPlayingState(false);
    setLoadingState(false);
    if (emitStop) {
      window.dispatchEvent(new CustomEvent("tts-stop"));
    }
  };

  const playBlobUrl = async (blobUrl: string): Promise<void> => {
    stopAudioElement();

    const el = new Audio(blobUrl);
    el.preload = "auto";
    el.volume = 1;
    audioRef.current = el;

    el.onplay = () => setPlayingState(true);
    el.onplaying = () => {
      setLoadingState(false);
      setPlayingState(true);
    };
    el.onpause = () => {
      if (!el.ended) setPlayingState(false);
    };
    el.onerror = () => {
      console.error("IndependentTtsButton: chunk playback error");
      setPlayingState(false);
      void apiRef.current.playNextChunk();
    };
    el.onended = () => {
      void apiRef.current.playNextChunk();
    };

    window.dispatchEvent(
      new CustomEvent("tts-play", {
        detail: {
          audioUrl: blobUrl,
          isPlaying: true,
          audioElement: el,
        },
      }),
    );

    await el.play();
  };

  const playNextChunk = async (): Promise<void> => {
    if (!sessionActiveRef.current) return;

    const index = nextPlayIndexRef.current;
    const blobUrl = chunkBlobUrlsRef.current.get(index);

    if (blobUrl) {
      nextPlayIndexRef.current = index + 1;
      setLoadingState(false);
      try {
        await playBlobUrl(blobUrl);
      } catch (e) {
        console.error("IndependentTtsButton: play failed", e);
        resetSession();
      }
      return;
    }

    if (receivedLastRef.current) {
      const owner = sessionOwnerRef.current;
      sessionActiveRef.current = false;
      clearFirstChunkTimer();
      if (owner) {
        releaseTtsAudioSession(owner);
        sessionOwnerRef.current = null;
      }
      setPlayingState(false);
      setLoadingState(false);
      window.dispatchEvent(new CustomEvent("tts-stop"));
      return;
    }

    setPlayingState(false);
    setLoadingState(true);
  };

  const enqueueChunk = async (chunk: TtsAudioChunkEvent): Promise<void> => {
    if (!sessionActiveRef.current) return;
    if (chunkBlobUrlsRef.current.has(chunk.index)) return;

    try {
      const blob = await fetchAuthenticatedFileBlob(chunk.audio_url, {
        timeoutMs: 45_000,
      });
      if (!sessionActiveRef.current) return;
      const blobUrl = URL.createObjectURL(blob);
      createdUrlsRef.current.push(blobUrl);
      chunkBlobUrlsRef.current.set(chunk.index, blobUrl);

      if (chunk.is_last) {
        receivedLastRef.current = true;
      }

      if (!isPlayingRef.current && chunk.index === nextPlayIndexRef.current) {
        await playNextChunk();
      }
    } catch (e) {
      console.error("IndependentTtsButton: failed to fetch chunk", e);
      if (sessionActiveRef.current) {
        toast.error("Could not load audio. Please try again.", {
          autoClose: 4000,
        });
        resetSession();
      }
    }
  };

  apiRef.current = {
    setLoadingState,
    setPlayingState,
    playNextChunk,
    enqueueChunk,
    resetSession,
  };

  // Stable handlers for the global bus (avoid stale closures across re-renders).
  const onChunkRef = useRef<(payload: TtsAudioChunkEvent) => void>(() => {});
  const onErrorRef = useRef<(payload: TtsAudioErrorEvent) => void>(() => {});

  onChunkRef.current = (payload: TtsAudioChunkEvent) => {
    if (!sessionActiveRef.current) return;
    if (!sameTtsSession(payload, messageIdRef.current, tabIdRef.current)) {
      return;
    }
    if (!gotFirstChunkRef.current) {
      gotFirstChunkRef.current = true;
      clearFirstChunkTimer();
    }
    playChainRef.current = playChainRef.current
      .then(() => apiRef.current.enqueueChunk(payload))
      .catch((e) =>
        console.error("IndependentTtsButton: chunk queue error", e),
      );
  };

  onErrorRef.current = (payload: TtsAudioErrorEvent) => {
    if (!sessionActiveRef.current) return;
    if (!sameTtsSession(payload, messageIdRef.current, tabIdRef.current)) {
      return;
    }
    console.error("IndependentTtsButton: tts_audio_error", payload.error);
    toast.error(payload.error || "Audio generation failed. Please try again.", {
      autoClose: 4000,
    });
    apiRef.current.resetSession();
  };

  const busChunkHandler = useRef((payload: TtsAudioChunkEvent) => {
    onChunkRef.current(payload);
  }).current;
  const busErrorHandler = useRef((payload: TtsAudioErrorEvent) => {
    onErrorRef.current(payload);
  }).current;

  // Attach socket listeners once; only release if this instance owns the session.
  useEffect(() => {
    ensureTtsAudioSocketAttached();
    return () => {
      clearFirstChunkTimer();
      const owner = sessionOwnerRef.current;
      if (owner && isTtsAudioSessionOwner(owner)) {
        apiRef.current.resetSession();
      }
    };
  }, []);

  // If message/tab changes while this button owns playback, stop it.
  useEffect(() => {
    const owner = sessionOwnerRef.current;
    if (owner && isTtsAudioSessionOwner(owner)) {
      apiRef.current.resetSession();
    }
  }, [message.message_id, tabId]);

  useEffect(() => {
    const onPlayerClosed = () => {
      const owner = sessionOwnerRef.current;
      if (owner && isTtsAudioSessionOwner(owner)) {
        apiRef.current.resetSession(false);
      }
    };
    const onSessionReplaced = (event: Event) => {
      const nextOwner = (event as CustomEvent<{ owner: TtsSessionOwner }>).detail
        ?.owner;
      const owner = sessionOwnerRef.current;
      if (!owner || owner === nextOwner) return;
      // Another speaker took over — clear local UI only (do not release theirs).
      sessionOwnerRef.current = null;
      sessionActiveRef.current = false;
      receivedLastRef.current = false;
      nextPlayIndexRef.current = 0;
      gotFirstChunkRef.current = false;
      clearFirstChunkTimer();
      stopAudioElement();
      revokeCreatedUrls();
      setPlayingState(false);
      setLoadingState(false);
    };
    window.addEventListener("tts-player-closed", onPlayerClosed);
    window.addEventListener("tts-session-replaced", onSessionReplaced);
    return () => {
      window.removeEventListener("tts-player-closed", onPlayerClosed);
      window.removeEventListener("tts-session-replaced", onSessionReplaced);
    };
  }, []);

  const handleClick = async () => {
    if (disabled) return;
    if (!isOnline || isBrowserOffline()) {
      notifyOfflineActionBlocked();
      return;
    }

    const speakable =
      speakableTextForVersion !== undefined
        ? speakableTextForVersion
        : (message.title || "") +
          "\n" +
          (message.text || "") +
          " " +
          (message.reply || "");

    if (tabId && !normalizeText(speakable)) return;

    if (sessionActiveRef.current || isPlayingRef.current || isLoadingRef.current) {
      resetSession();
      return;
    }

    // Only block on the socket when it is not ready yet (cold page load).
    // If already connected, this returns immediately — no extra delay.
    const socket = await waitForTtsSocketReady(5_000);
    if (!socket) {
      console.error("IndependentTtsButton: socket not ready");
      toast.error("Not connected. Wait a moment and try again.", {
        autoClose: 4000,
      });
      return;
    }

    if (!ensureTtsAudioSocketAttached()) {
      console.error("IndependentTtsButton: failed to attach TTS socket listeners");
      toast.error("Audio is unavailable right now. Please try again.", {
        autoClose: 4000,
      });
      return;
    }

    const owner = Symbol("tts-session");
    sessionOwnerRef.current = owner;
    sessionActiveRef.current = true;
    receivedLastRef.current = false;
    nextPlayIndexRef.current = 0;
    gotFirstChunkRef.current = false;
    chunkBlobUrlsRef.current.clear();
    setLoadingState(true);

    // Claim session BEFORE HTTP so the first chunk is not missed.
    claimTtsAudioSession(owner, busChunkHandler, busErrorHandler);

    clearFirstChunkTimer();
    firstChunkTimerRef.current = window.setTimeout(() => {
      if (!sessionActiveRef.current || gotFirstChunkRef.current) return;
      console.error(
        "IndependentTtsButton: timed out waiting for tts_audio_chunk",
        { messageId: message.message_id, tabId },
      );
      toast.error(
        "Audio did not start. The server may not have reached this tab — try again.",
        { autoClose: 5000 },
      );
      apiRef.current.resetSession();
    }, FIRST_CHUNK_TIMEOUT_MS);

    const backendTabId = tabId ? toBackendTabId(tabId) : undefined;

    try {
      await messageToAudio(message.message_id, backendTabId);
    } catch (e) {
      console.error("IndependentTtsButton: failed to start TTS", e);
      toast.error("Could not start audio. Please try again.", {
        autoClose: 4000,
      });
      resetSession();
    }
  };

  return (
    <Box
      component="div"
      onClick={disabled ? undefined : handleClick}
      sx={{
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.38 : 1,
        pointerEvents: disabled ? "none" : "auto",
      }}
    >
      {loading ? (
        <CircularProgress size={25} sx={{ color: "#A0A3B1" }} />
      ) : isPlaying ? (
        <StopIcon size={25} color="#06B6D4" />
      ) : (
        <SpeakerIcon size={25} color="#A0A3B1" />
      )}
    </Box>
  );
};

export default IndependentTtsButton;
